import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import AuthModal from 'components/AuthModal/AuthModal';
import useChatUnread from 'hooks/useChatUnread';
import styles from './MobileBottomNav.module.scss';

// Icons
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
  </svg>
);

const HomeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10.55 2.532a2.25 2.25 0 012.9 0l7.75 6.563A2.25 2.25 0 0122 10.818V20a2 2 0 01-2 2h-4a1 1 0 01-1-1v-5h-6v5a1 1 0 01-1 1H4a2 2 0 01-2-2v-9.182a2.25 2.25 0 01.8-1.724l7.75-6.562z"/>
  </svg>
);

const PersonalPanelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 3.25A1.25 1.25 0 008.25 9.5h2.5A1.25 1.25 0 0012 8.25v-.5A1.25 1.25 0 0010.75 6.5h-2.5A1.25 1.25 0 007 7.75v.5zm5 0A1.25 1.25 0 0013.25 9.5h2.5A1.25 1.25 0 0017 8.25v-.5A1.25 1.25 0 0015.75 6.5h-2.5A1.25 1.25 0 0012 7.75v.5zM7 13.75A1.25 1.25 0 008.25 15h2.5A1.25 1.25 0 0012 13.75v-.5A1.25 1.25 0 0010.75 12h-2.5A1.25 1.25 0 007 13.25v.5zm5.95-1.42a.9.9 0 011.17-.47l.45.2.2-.45a.9.9 0 111.64.73l-.2.45.45.2a.9.9 0 11-.73 1.64l-.45-.2-.2.45a.9.9 0 11-1.64-.73l.2-.45-.45-.2a.9.9 0 01-.44-1.17z"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    {/* back bubble */}
    <path d="M17 12h2c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v2h8c1.66 0 3 1.34 3 3v3z" opacity="0.55"/>
    {/* front bubble with tail */}
    <path d="M15 8H3c-1.1 0-2 .9-2 2v8l3.64-3H15c1.1 0 2-.9 2-2v-3c0-1.1-.9-2-2-2z"/>
  </svg>
);

const ProfileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const MobileBottomNav = ({ onMenuClick, onPlusClick, onLoginClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const totalUnread = useChatUnread(!!user);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.detail) setAuthOpen(true); };
    window.addEventListener('openAuth', handler);
    return () => window.removeEventListener('openAuth', handler);
  }, []);

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleChatsClick = () => {
    navigate('/chats');
  };

  const handlePersonalPanelClick = () => {
    if (user) {
      navigate('/personal-panel');
    } else {
      setAuthOpen(true);
    }
  };

  const handleProfileClick = () => {
    if (user) {
      navigate('/profile');
    } else {
      setAuthOpen(true);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className={styles.mobileNav}>
      <div className={styles.navBar}>
        {/* Menu Button */}
        <button 
          className={styles.navButton} 
          onClick={onMenuClick}
        >
          <MenuIcon />
        </button>

        {/* Home Button */}
        <button 
          className={`${styles.navButton} ${isActive('/') ? styles.active : ''}`}
          onClick={handleHomeClick}
        >
          <HomeIcon />
        </button>

        {/* Personal Panel Button */}
        <button
          className={`${styles.navButton} ${isActive('/personal-panel') ? styles.active : ''}`}
          onClick={handlePersonalPanelClick}
          title={user ? 'Мой хаб' : 'Войти'}
        >
          <PersonalPanelIcon />
        </button>

        {/* Chats Button */}
        <button 
          className={`${styles.navButton} ${isActive('/chats') ? styles.active : ''}`}
          onClick={handleChatsClick}
        >
          <span className={styles.iconWrap}>
            <ChatIcon />
            {totalUnread > 0 && (
              <span className={styles.unreadDot}>
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </span>
        </button>

        {/* Profile Button */}
        <button 
          className={`${styles.navButton} ${isActive('/profile') ? styles.active : ''}`}
          onClick={handleProfileClick}
          title={user ? 'Профиль' : 'Войти'}
        >
          <ProfileIcon />
        </button>

        {/* Plus Button */}
        <button 
          className={`${styles.navButton} ${styles.plusButton}`}
          onClick={onPlusClick}
        >
          <PlusIcon />
        </button>
      </div>

      {/* Labels container */}

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <div className={styles.labelsContainer}>
        <span className={styles.label}>Меню</span>
        <span className={`${styles.label} ${isActive('/') ? styles.activeLabel : ''}`}>Главная</span>
        <span className={`${styles.label} ${isActive('/personal-panel') ? styles.activeLabel : ''}`}>Мой хаб</span>
        <span className={`${styles.label} ${isActive('/chats') ? styles.activeLabel : ''}`}>Чаты</span>
        <span className={`${styles.label} ${isActive('/profile') ? styles.activeLabel : ''}`}>{user ? 'Профиль' : 'Войти'}</span>
        <span className={styles.label}>Ещё</span>
      </div>
    </div>
  );
};

export default MobileBottomNav;
